import { z } from "zod";

import type { AppointmentType, SchedulingAction } from "@/shared/models/appointment";

export interface Address {
  line1: string | null;
  line2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;
}

export const agentPatientSchema = z.object({
  id: z.string().min(1),
  givenName: z.string().min(1),
  familyName: z.string().min(1),
  dateOfBirth: z.string().date(),
});

export type Patient = z.infer<typeof agentPatientSchema>;

export enum PatientMatchMethod {
  EHR_ONLY = "EHR_ONLY",
  FUZZY_NAME_AND_DOB = "FUZZY_NAME_AND_DOB",
  NAME_AND_DOB = "NAME_AND_DOB",
  PHONE_AND_DOB = "PHONE_AND_DOB",
  PHONE_AND_NAME = "PHONE_AND_NAME",
}

export type CreatePatientFromIntakeResponse =
  | { status: "created"; patient: Patient }
  | { status: "carrier_not_accepted"; carrier: string };

export const agentCoverageSchema = z.object({
  payer: z.string().min(1),
  plan: z.string().nullable(),
});

export type Coverage = z.infer<typeof agentCoverageSchema>;

export const searchPatientsResponseSchema = z.object({
  patient: agentPatientSchema,
  matchMethod: z.nativeEnum(PatientMatchMethod),
  coverage: agentCoverageSchema.nullable(),
});

export type SearchPatientsResponse = z.infer<typeof searchPatientsResponseSchema>;

export const patientNotFoundResponseSchema = z.object({
  error: z.string().min(1),
  code: z.literal("patient_not_found"),
});

export interface OpenAppointment {
  appointmentId: string;
  appointmentType: string;
  appointmentTypeId: string;
  date: string;
  startTime: string;
  duration: number;
  departmentId: string;
  providerId: string;
  patientAppointmentTypeName?: string;
}

export interface SearchOpenAppointmentsResponse {
  appointments: OpenAppointment[];
}

export interface ConfirmedAppointment {
  appointmentId: string;
  /** Internal Valerie appointment row id (for post-booking workflows). */
  valerieAppointmentId?: string;
  appointmentType?: string;
  appointmentTypeId?: string;
  dateTime: string;
  duration?: number;
  departmentId: string;
  /** Internal Valerie location ID. `departmentId` remains the EHR-facing location identifier. */
  locationId?: string | null;
  locationName?: string | null;
  providerId?: string;
  providerDisplayName: string | null;
  patientId: string;
  status?: string;
}

export interface BookAppointmentResponse {
  confirmedAppointment: ConfirmedAppointment & {
    locationId: string;
    locationName: string;
  };
}

export interface CancelAppointmentResponse {
  cancelledAppointment: ConfirmedAppointment;
}

export interface BookedAppointment {
  id: string;
  appointmentDatetime: string;
  type: string;
  reason: string | null;
  status: string | null;
  ehrId: string;
  /** Signed application-owned action reference. Callers must store it opaquely and keep it from the model. */
  appointmentActionId?: string;
  practitionerId: string | null;
  practitionerFirstName: string | null;
  practitionerLastName: string | null;
  practitionerDisplayName: string | null;
  durationMinutes?: number | null;
  locationId?: string | null;
  locationName?: string | null;
  // Actions the agent may take on THIS appointment (VAL-2214). Present only for groups that restrict
  // modification by resource; when set, the agent must not reschedule/cancel unless the action is
  // listed here (confirm is always listed). Undefined = no per-appointment restriction.
  allowedActions?: SchedulingAction[];
}

export interface GetBookedAppointmentsResponse {
  appointments: BookedAppointment[];
  searchedDateRanges: string[];
}

export interface AvailabilityAppointmentSlot {
  appointmentId: string;
  appointmentType: string;
  ehrAppointmentTypeId: string | null;
  start: string;
  end: string;
  practitionerId: string | null;
  practitionerFirstName: string | null;
  practitionerLastName: string | null;
  practitionerDisplayName: string | null;
  locationId: string | null;
  locationName: string | null;
}

export interface AvailabilityResponse {
  slots: AvailabilityAppointmentSlot[];
  searchedDateRanges: string[];
}

export interface Location {
  locationId: string;
  locationName: string;
}

export interface GetLocationsResponse {
  locations: Location[];
}

export interface RescheduleAppointmentResponse {
  rescheduledAppointment: BookedAppointment;
}

export enum RecommendationType {
  ANY = "any",
  ESTABLISHED_PATIENT = "establishedPatientScheduling",
  NEW_PATIENT = "newPatientScheduling",
  RESCHEDULING = "rescheduling",
}

export interface EligibilityProviderResult {
  providerName: string;
  practitionerId: string;
  isEligible: boolean;
  reasons: string[];
  agentInstructions?: string;
  // The patient's soonest existing upcoming appointment, when they already have one
  // can be combined with agentInstructions to inform agent how to use this data
  upcomingAppointment?: { date: string; providerName: string };
  nextAppointmentDetails?: {
    approximateDate: string;
    appointmentType?: AppointmentType | null;
  };
}

export interface CheckEligibilityResponse {
  providerEligibilityResults: EligibilityProviderResult[];
  reason?: string | null;
  diagnosis_list?: Array<{
    diagnosis_text: string | null;
  }> | null;
}

export interface EligibilityLocationResult {
  locationName: string | null;
  locationId: string;
  isEligible: boolean;
  reasons: string[];
  agentInstructions?: string;
}

/**
 * Output of the agentic eligibility sub-agent: a flat, isEligible-flagged determination of which
 * candidate providers/locations the patient may be scheduled with for the given scenario. Consumed
 * by the ranking sub-agent (which uses only the isEligible entries).
 */
export interface AgenticEligibilityResponse {
  recommendationType: RecommendationType;
  providerResults: EligibilityProviderResult[];
  locationResults: EligibilityLocationResult[];
}

/** A single ranked open slot. appointmentSlot is the verbatim slot returned by getProviderAvailability. */
export interface RankedAppointment {
  rank?: number | null;
  providerName?: string | null;
  locationName?: string | null;
  reasoning?: string | null;
  appointmentSlot: AvailabilityAppointmentSlot;
}

/** Why the ranking sub-agent produced zero recommendations. Mutually exclusive with a non-empty list. */
export interface RankingNoRecommendationsReason {
  category: "no_open_slots" | "no_eligible_providers_or_locations";
  explanation: string;
}

/**
 * Output of the agentic ranking sub-agent: the ranked open appointments for the patient. Exactly one
 * of `recommendations` (non-empty) / `noRecommendationsReason` (non-null) is populated per response.
 */
export interface AgenticRankedAppointmentsResponse {
  recommendations: RankedAppointment[];
  noRecommendationsReason: RankingNoRecommendationsReason | null;
}
